import { defineStore } from 'pinia'

import { StoreState } from '@shared/models/interfaces/store'
import {
  CreateNewGroupDataDTO,
  CreateNewGroupDTO,
  GetCountsDTO,
  GroupsIndexDTO,
  SetCurrentGroupDTO,
  UsageLimitsShowDTO,
} from '@shared/models/proto/dto/apps-header'
import {
  CountsRO,
  GroupItemRO,
  GroupsIndexRO,
  SetCurrentGroupRO,
  UsageLimitsItemRO,
} from '@shared/models/proto/ro/apps-header'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { Counts_Data } from '@protos/apps/hd/response/apps_header/counts_pb'
import { UsageLimitExtentType } from '@protos/apps/hd/usage_limit_extent_type_pb'
import { UsageLimit_Type } from '@protos/apps/usage_limit_pb'

interface State {
  groups: StoreState<GroupsIndexRO>
  counts: StoreState<Counts_Data>
  usageLimit: StoreState<UsageLimitsItemRO>
}

const state = (): State => ({
  groups: getInitialState([]),
  counts: getInitialState(),
  usageLimit: getInitialState(),
})

export const apiTransport = new ApiTransport(resourcePath, 'hd')

export const useAppsHeaderStore = defineStore({
  id: 'toolbar-groups',

  state,

  actions: {
    async fetchGroups(params: Pick<GroupsIndexDTO, 'page' | 'perPage'>) {
      this.groups = updateState(this.groups)

      try {
        const { data, meta } = await apiTransport.get<
          GroupsIndexDTO,
          GroupsIndexRO
        >('/apps_header/groups', new GroupsIndexDTO(params), GroupsIndexRO)

        const { data: currentGroupList = [] } = this.groups.data ?? {}
        const updatedGroupsList = [...currentGroupList, ...data]

        this.groups = updateState(
          this.groups,
          new GroupsIndexRO({
            data: updatedGroupsList,
            meta,
          })
        )

        return { data: updatedGroupsList, meta }
      } catch (error) {
        this.groups = updateState(this.groups, error as Error)
        throw error
      }
    },

    async createGroup(
      payload: Pick<
        CreateNewGroupDataDTO,
        | 'groupName'
        | 'addressLine1'
        | 'groupPhone'
        | 'groupWebsite'
        | 'addressLine2'
        | 'city'
        | 'zip'
      >
    ) {
      try {
        const { data } = await apiTransport.post<
          CreateNewGroupDTO,
          GroupItemRO
        >(
          '/apps_header/create_group',
          new CreateNewGroupDTO({ data: payload }),
          CreateNewGroupDTO,
          GroupItemRO
        )

        const { data: currentGroupList = [], meta } = this.groups.data ?? {}
        if (meta?.page === meta?.totalPages) {
          const updatedGroupsList = [...currentGroupList, data!]

          this.groups = updateState(
            this.groups,
            new GroupsIndexRO({
              data: updatedGroupsList,
              meta,
            })
          )
        }
      } catch (e) {
        throw Promise.reject(e)
      }
    },

    async setCurrentGroup(groupId: number) {
      const { url } = await apiTransport.get<
        SetCurrentGroupDTO,
        SetCurrentGroupRO
      >(
        '/apps_header/set_current_group',
        new SetCurrentGroupDTO({ groupId }),
        SetCurrentGroupRO
      )

      return url
    },

    async fetchUsageLimits(types: UsageLimit_Type[], chatWidgetId?: number) {
      this.usageLimit = updateState(this.usageLimit)

      try {
        const data = await apiTransport.get<
          UsageLimitsShowDTO,
          UsageLimitsItemRO
        >(
          '/usage_limits_status',
          new UsageLimitsShowDTO({ types, chatWidgetId }),
          UsageLimitsItemRO
        )

        this.usageLimit = updateState(this.usageLimit, data)
        return data
      } catch (error) {
        this.usageLimit = updateState(this.usageLimit, error as Error)
        throw error
      }
    },

    async fetchNotificationsCount() {
      const { data } = (await apiTransport.get<GetCountsDTO, CountsRO>(
        '/apps_header/get_counts',
        new GetCountsDTO(),
        CountsRO
      )) ?? { data: {} }

      this.counts = updateState(this.counts, data)
    },

    resetUsageLimits() {
      this.usageLimit = getInitialState()
    },
  },

  getters: {
    isOverLimit: (state) =>
      state.usageLimit.data?.data?.usageLimitExtentType ===
      UsageLimitExtentType.USAGE_LIMIT_EXCEEDED,
    isLimitReached: (state) =>
      state.usageLimit.data?.data?.usageLimitExtentType ===
      UsageLimitExtentType.USAGE_LIMIT_REACHED,

    announcementsCount: (state) =>
      state.counts?.data?.announcementNotificationsCount ?? 0,
    notificationsCount: (state) =>
      state.counts?.data?.webNotificationsCount ?? 0,
  },
})
